import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { Material } from '@/features/traceability/type/Material';
import { Badge } from '@/components/ui/badge';
import { ComboboxPopup } from './combobox/ComboboxPopup';
import { useCombobox } from './combobox/useCombobox';

type MaterialComboboxProps = {
  materials: Material[];
  selectedId: number | null;
  onSelect: (id: number | null) => void;
  placeholder: string;
  searchPlaceholder: string;
  noResults: string;
  /** Accessible name of the clear (×) button. */
  clearLabel: string;
  error?: string | null;
  isoOptions: Array<{ value: string; label: string }>;
  id?: string;
};

/**
 * Select-only material picker: a button showing the chosen material, opening a panel with a
 * search field and the list. List, keyboard and outside-click handling come from the shared
 * `useCombobox` / `ComboboxPopup` (same as the tool and reference pickers).
 */
export function MaterialCombobox({
  materials,
  selectedId,
  onSelect,
  placeholder,
  searchPlaceholder,
  noResults,
  clearLabel,
  error,
  isoOptions,
  id = 'material-combobox',
}: MaterialComboboxProps) {
  const [query, setQuery] = useState('');
  const triggerRef = useRef<HTMLButtonElement>(null);

  const selected = materials.find((m) => m.id === selectedId) ?? null;

  const filtered = useMemo(() => {
    const q = query.toLowerCase();
    if (!q) return materials;
    return materials.filter(
      (m) => m.name.toLowerCase().includes(q) || (m.designation?.toLowerCase().includes(q) ?? false)
    );
  }, [materials, query]);

  const combobox = useCombobox<Material>({
    id,
    options: filtered,
    onPick: (material) => onSelect(material.id ?? null),
  });
  const { open } = combobox;

  // A closed panel forgets its search.
  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  const isoCategoryLabel = (path: string) => {
    const found = isoOptions.find((o) => o.value === path);
    return found ? found.label : path;
  };

  function toggle() {
    if (open) {
      combobox.close();
      return;
    }
    combobox.setOpen(true);
    // Open on the chosen material, so the keyboard starts from it.
    combobox.setActive(materials.findIndex((m) => m.id === selectedId));
  }

  return (
    <div ref={combobox.containerRef} className="relative">
      <button
        ref={triggerRef}
        type="button"
        onClick={toggle}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown' && !open) {
            event.preventDefault();
            toggle();
          }
        }}
        aria-invalid={error ? true : undefined}
        className={cn(
          // the field look shared by Input / Select / TextCombobox
          'flex min-h-9 w-full items-center justify-between gap-2 rounded-lg border border-input bg-transparent px-3 py-1.5 text-left text-sm shadow-xs outline-none transition-[color,box-shadow] focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 dark:bg-input/30',
          selected ? 'pr-16 text-foreground' : 'text-muted-foreground',
          error && 'border-destructive'
        )}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {selected ? (
          <span className="flex flex-wrap items-center gap-1.5 min-w-0">
            <span className="font-semibold truncate">{selected.name}</span>
            {selected.isoCategory && (
              <Badge tone="primary" size="sm">
                {isoCategoryLabel(selected.isoCategory)}
              </Badge>
            )}
            {selected.designation && (
              <span className="font-mono text-xs text-muted-foreground truncate">
                {selected.designation}
              </span>
            )}
          </span>
        ) : (
          <span>{placeholder}</span>
        )}
        <ChevronDown
          className={cn(
            'h-4 w-4 shrink-0 text-muted-foreground transition-transform',
            open && 'rotate-180'
          )}
          aria-hidden
        />
      </button>
      {selected && (
        <button
          type="button"
          aria-label={clearLabel}
          onClick={() => {
            onSelect(null);
            combobox.close();
          }}
          className="absolute right-9 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-foreground outline-none hover:text-destructive focus-visible:ring-[3px] focus-visible:ring-ring/50"
        >
          <X className="h-3.5 w-3.5" aria-hidden />
        </button>
      )}

      {error && <p className="mt-1 text-xs text-destructive">{error}</p>}

      {open && (
        <ComboboxPopup
          combobox={combobox}
          options={filtered}
          optionKey={(material) => String(material.id ?? material.name)}
          emptyLabel={noResults}
          top={
            <div className="border-b border-border/40 p-2">
              <input
                autoFocus
                type="text"
                role="combobox"
                aria-autocomplete="list"
                aria-expanded
                aria-controls={combobox.listboxId}
                aria-activedescendant={
                  combobox.activeIndex >= 0 ? combobox.optionId(combobox.activeIndex) : undefined
                }
                aria-label={searchPlaceholder}
                value={query}
                onChange={(event) => {
                  setQuery(event.target.value);
                  combobox.setActive(0);
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Escape') triggerRef.current?.focus();
                  combobox.onKeyDown(event);
                  if (event.key === 'Enter') triggerRef.current?.focus();
                }}
                placeholder={searchPlaceholder}
                className="w-full rounded-lg border border-input bg-transparent px-3 py-1.5 text-sm text-foreground outline-none placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
              />
            </div>
          }
          renderOption={(material) => (
            <span className="flex min-w-0 flex-col gap-0.5">
              <span className="flex flex-wrap items-center gap-1.5">
                <span className="text-sm font-semibold">{material.name}</span>
                {material.isoCategory && (
                  <Badge tone="primary" size="sm">
                    {isoCategoryLabel(material.isoCategory)}
                  </Badge>
                )}
              </span>
              {material.designation && (
                <span className="font-mono text-xs text-muted-foreground">
                  {material.designation}
                </span>
              )}
            </span>
          )}
        />
      )}
    </div>
  );
}
